import { FC, memo, useEffect, useMemo, useRef, useState } from 'react';
import s from './ProjectForm.module.scss';
import { Button } from 'components/common/Button';
import { InputField } from 'components/common/InputField';
import { Modal } from 'components/common/Modal/Modal';
import { PortalComponent } from 'components/common/PortalComponent';
import { useTranslation } from 'react-i18next';
import { ReactComponent as Paperclip } from 'images/paper-clip.svg';
import { AttachFile } from 'components/common/AttachFile/AttachFile';
import { Project } from 'types/project';
import { useSelector } from 'react-redux';
import { selectFileOptions } from 'store/features/chatSettings/selectors';
import {
  DEFAULT_ACCEPTED_FILE_TYPES,
  DEFAULT_MAX_FILE_COUNT,
  DEFAULT_MAX_TOTAL_FILE_SIZE,
} from 'store/features/chatSettings/utils';

const icons = {
  paperclip: <Paperclip />,
};

export type ProjectFormSubmitData = {
  name: string;
  instruction: string;
  newFiles: File[];
  removedFileIds: string[];
};

type ProjectFormProps = {
  title: string;
  name?: string;
  instruction?: string;
  files?: Project['files'];
  textSubmit: string;
  onClose: () => void;
  onSubmit: (data: ProjectFormSubmitData) => void;
};

export const ProjectForm: FC<ProjectFormProps> = memo(
  ({
    title,
    name = '',
    instruction = '',
    files = [],
    textSubmit,
    onClose,
    onSubmit,
  }) => {
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const { limits, supportedFileExtensions } =
      useSelector(selectFileOptions) || {};

    const maxTotalFileSize =
      limits?.maxFileSizeBytes ?? DEFAULT_MAX_TOTAL_FILE_SIZE;
    const maxFileCount = limits?.maxFilesPerProject ?? DEFAULT_MAX_FILE_COUNT;
    const acceptedFileTypes =
      supportedFileExtensions?.join(',') ?? DEFAULT_ACCEPTED_FILE_TYPES;

    const [projectName, setProjectName] = useState<string>(name);
    const [projectInstruction, setProjectInstruction] =
      useState<string>(instruction);

    const [newFiles, setNewFiles] = useState<{ id: string; file: File }[]>([]);
    const [removedFileIds, setRemovedFileIds] = useState<string[]>([]);

    const displayedFiles = useMemo(() => {
      const result: Array<{ id: string; name: string; size: number }> = [];

      for (const file of files) {
        if (removedFileIds.includes(file.fileId)) continue;

        result.push({
          id: file.fileId,
          name: file.filename,
          size: file.size,
        });
      }

      for (const { id, file } of newFiles) {
        result.push({
          id,
          name: file.name,
          size: file.size,
        });
      }

      return result;
    }, [files, newFiles, removedFileIds]);

    const [warn, setWarn] = useState<string | null>(null);

    const handleChangeInput: React.ChangeEventHandler<HTMLInputElement> = (
      e,
    ) => {
      setProjectName(e.target.value);
    };

    const handleChangeInstruction: React.ChangeEventHandler<
      HTMLTextAreaElement
    > = (e) => {
      setProjectInstruction(e.target.value);
    };

    const handleFileSelect: React.ChangeEventHandler<HTMLInputElement> = (
      e,
    ) => {
      const selected = e.target.files;
      if (!selected?.length) return;

      setNewFiles((prev) => {
        const addedFiles = Array.from(selected).map((file) => ({
          id: crypto.randomUUID(),
          file,
        }));

        const result = [...prev, ...addedFiles];

        const filesSize = files.reduce((sum, file) => {
          if (removedFileIds.includes(file.fileId)) return sum - file.size;
          return sum + file.size;
        }, 0);
        const newFilesSize = result.reduce(
          (sum, { file }) => sum + file.size,
          0,
        );
        const totalFilesSize = filesSize + newFilesSize;

        if (totalFilesSize > maxTotalFileSize) {
          setWarn(
            t('project.errors.maxTotalFileSize', {
              size: maxTotalFileSize / 1024 / 1024,
            }),
          );
          return prev;
        }

        const filesCount = files.length - removedFileIds.length;
        const newFilesCount = result.length;
        const totalFilesCount = filesCount + newFilesCount;

        if (totalFilesCount > maxFileCount) {
          setWarn(t('project.errors.maxFileCount', { count: maxFileCount }));

          return result.slice(0, maxFileCount - filesCount);
        }

        return result;
      });
    };

    const handleRemoveFile = (fileId: string) => {
      if (files.some((file) => file.fileId === fileId)) {
        setRemovedFileIds((prev) => [...prev, fileId]);
      } else {
        setNewFiles((prev) => prev.filter(({ id }) => id !== fileId));
      }
    };

    const handleSubmit: React.MouseEventHandler<HTMLButtonElement> = async (
      e,
    ) => {
      e.stopPropagation();
      try {
        // if (!projectName.trim() || isCreateLoading) return;

        // await createProject(projectName).unwrap();
        // console.log(attachedFiles);

        await onSubmit({
          name: projectName,
          instruction: projectInstruction,
          newFiles: newFiles.map(({ file }) => file),
          removedFileIds,
        });

        // setProjectName('');
        // setAttachedFiles([]);
        // onClose();
      } catch {}
    };

    const isNameChanged = name !== projectName.trim();
    const isInstructionChanged = instruction !== projectInstruction;
    const hasNewFiles = newFiles.length > 0;
    const hasRemovedFiles = removedFileIds.length > 0;
    const isDisabled =
      projectName.trim() === '' ||
      (!isNameChanged &&
        !isInstructionChanged &&
        !hasNewFiles &&
        !hasRemovedFiles);

    useEffect(() => {
      if (warn) {
        const timeout = setTimeout(() => {
          setWarn(null);
        }, 3000);
        return () => clearTimeout(timeout);
      }
      return undefined;
    }, [warn]);

    return (
      <PortalComponent>
        <Modal title={title} onClose={onClose}>
          <div className={s['new-project']}>
            <InputField
              title={t('project.name')}
              value={projectName}
              onChange={handleChangeInput}
              placeholder={t('project.placeholders.name')}
            />

            <label htmlFor='instructions' className={s['instructions']}>
              <span>{t('common.instructions')}</span>

              <div className={s['textarea-wrp']}>
                <textarea
                  id='instructions'
                  value={projectInstruction}
                  onChange={handleChangeInstruction}
                  placeholder={t('project.placeholders.instructions')}
                />
              </div>
            </label>

            <div className={s['attachments']}>
              <input
                ref={fileInputRef}
                type='file'
                accept={acceptedFileTypes}
                multiple
                className={s['attachments__file-input']}
                onChange={handleFileSelect}
                aria-hidden
              />

              {displayedFiles.length > 0 && (
                <div className={s['attachments__files']}>
                  {displayedFiles.map((file, _i) => (
                    <AttachFile
                      key={file.id}
                      name={file.name}
                      onRemove={() => handleRemoveFile(file.id)}
                      size={file.size}
                    />
                  ))}
                </div>
              )}
            </div>

            {warn && (
              <span style={{ color: 'var(--color-danger)' }}>{warn}</span>
            )}

            <footer className={s['footer']}>
              <Button
                className={s['add-files-btn']}
                onClick={() => fileInputRef.current?.click()}
              >
                {icons.paperclip}
                <span>{t('common.addFiles')}</span>
              </Button>

              <Button
                className={s['create-btn']}
                onClick={handleSubmit}
                disabled={isDisabled}
              >
                {textSubmit}
              </Button>
            </footer>
          </div>

          {/* {isCreateLoading || (isRenameLoading && <Loader />)} */}
        </Modal>
      </PortalComponent>
    );
  },
);

ProjectForm.displayName = 'ProjectForm';
